import { AxiosError } from 'axios';
import { toast } from 'sonner';

export interface APIErrorResponse {
  message: string;
  code?: string;
  errors?: Record<string, string[]>;
}

export function handleAPIError(
  error: unknown,
  customHandlers?: {
    [statusCode: number]: (error: AxiosError<APIErrorResponse>) => void;
  },
) {
  if (!isAxiosError(error)) {
    toast.error('Network error', {
      description: 'Please check your internet connection',
    });
    return;
  }

  const axiosError = error as AxiosError<APIErrorResponse>;

  if (!axiosError.response) {
    toast.error('Network error', {
      description: 'Could not connect to server',
    });
    return;
  }

  const status = axiosError.response.status;
  const data: any = axiosError.response.data;

  if (customHandlers?.[status]) {
    customHandlers[status](axiosError);
    return;
  }

  switch (status) {
    case 400:
      toast.error('Validation error', {
        description: data?.error?.message || 'Please check your input',
      });
      break;
    case 401:
      toast.error('Unauthorized', {
        description: 'Please log in again',
      });
      // Optionally redirect to login
      break;
    case 403:
      toast.error('Forbidden', {
        description: 'You do not have permission to perform this action',
      });
      break;
    case 404:
      toast.error('Not found', {
        description:
          data?.error?.message || 'The requested resource was not found',
      });
      break;
    case 409:
      toast.error('Conflict', {
        description: data?.error?.message || 'This resource already exists',
      });
      break;
    case 422:
      toast.error('Validation failed', {
        description: data?.error?.message || 'Please check your input',
      });
      break;
    case 500:
    case 502:
    case 503:
      toast.error('Server error', {
        description: 'Something went wrong. Please try again later.',
      });
      break;
    default:
      toast.error('Error', {
        description:
          data?.error?.message || axiosError.message || 'An error occurred',
      });
  }
}

function isAxiosError(error: unknown): error is AxiosError {
  return (error as AxiosError).isAxiosError === true;
}
